import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import Button from '../components/Button';
import Input from '../components/Input';
import Table, { Column } from '../components/Table';
import Modal from '../components/Modal';

interface Shift {
  id: string;
  code: string;
  name: string;
  clock_in_time: string;
  clock_out_time: string;
  normal_working_hours: number;
  ot_rate: number;
}

const EMPTY_FORM = {
  code: '',
  name: '',
  clockInTime: '',
  clockOutTime: '',
  normalWorkingHours: 8,
  lunchBreakDuration: 60,
  latenessGracePeriod: 0,
  latenessDeductionRate: 0,
  excludeGracePeriod: false,
  earlyOutGracePeriod: 0,
  earlyOutDeductionRate: 0,
  otRate: 1.5,
  otRoundUp: true,
  otRoundValue: 15,
  minimumOt: 0,
  maxOtHours: '',
  extraOtRate: '',
  halfDayHours: 4,
};

export default function Shifts() {
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState(EMPTY_FORM);

  useEffect(() => {
    fetchShifts();
  }, []);

  const fetchShifts = async () => {
    try {
      setLoading(true);
      const response = await api.get('/tms/shifts');
      setShifts(response.data.shifts || []);
    } catch (error) {
      console.error('Failed to fetch shifts:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCreateForm = () => {
    setEditingId(null);
    setFormData(EMPTY_FORM);
    setShowForm(true);
  };

  const openEditForm = async (shift: Shift) => {
    try {
      const res = await api.get(`/tms/shifts/${shift.id}`);
      const s = res.data.shift;
      setEditingId(shift.id);
      setFormData({
        code: s.code || '',
        name: s.name || '',
        clockInTime: (s.clock_in_time || '').slice(0, 5),
        clockOutTime: (s.clock_out_time || '').slice(0, 5),
        normalWorkingHours: s.normal_working_hours ?? 8,
        lunchBreakDuration: s.lunch_break_duration ?? 60,
        latenessGracePeriod: s.lateness_grace_period ?? 0,
        latenessDeductionRate: s.lateness_deduction_rate ?? 0,
        excludeGracePeriod: !!s.exclude_grace_period,
        earlyOutGracePeriod: s.early_out_grace_period ?? 0,
        earlyOutDeductionRate: s.early_out_deduction_rate ?? 0,
        otRate: s.ot_rate ?? 1.5,
        otRoundUp: !!s.ot_round_up,
        otRoundValue: s.ot_round_value ?? 15,
        minimumOt: s.minimum_ot ?? 0,
        maxOtHours: s.max_ot_hours ?? '',
        extraOtRate: s.extra_ot_rate ?? '',
        halfDayHours: s.half_day_hours ?? 4,
      });
      setShowForm(true);
    } catch (error) {
      alert('Failed to load shift details');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/tms/shifts/${editingId}`, formData);
      } else {
        await api.post('/tms/shifts', formData);
      }
      setShowForm(false);
      setEditingId(null);
      setFormData(EMPTY_FORM);
      fetchShifts();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to save shift');
    }
  };

  const columns: Column<Shift>[] = [
    { key: 'code', header: 'Code' },
    { key: 'name', header: 'Name', sortable: true },
    { key: 'clock_in_time', header: 'Clock In' },
    { key: 'clock_out_time', header: 'Clock Out' },
    { key: 'normal_working_hours', header: 'Working Hours', render: (row) => `${row.normal_working_hours}h` },
    { key: 'ot_rate', header: 'OT Rate', render: (row) => `${row.ot_rate}x` },
    {
      key: 'actions',
      header: 'Actions',
      render: (row) => (
        <div className="flex gap-3">
          <button onClick={() => openEditForm(row)} className="text-blue-600 hover:underline text-sm">
            Edit
          </button>
          <Link to={`/tms/shifts/${row.id}/weekdays`} className="text-blue-600 hover:underline text-sm">
            Weekdays
          </Link>
        </div>
      ),
    },
  ];

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Shift Management</h1>
        <Button onClick={openCreateForm}>Create Shift</Button>
      </div>

      <Table columns={columns} rows={shifts} loading={loading} emptyMessage="No shifts yet" />

      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title={editingId ? 'Edit Shift' : 'Create Shift'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Shift Code"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              required
              disabled={!!editingId}
            />
            <Input
              label="Shift Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
            <Input
              label="Clock In Time"
              type="time"
              value={formData.clockInTime}
              onChange={(e) => setFormData({ ...formData, clockInTime: e.target.value })}
            />
            <Input
              label="Clock Out Time"
              type="time"
              value={formData.clockOutTime}
              onChange={(e) => setFormData({ ...formData, clockOutTime: e.target.value })}
            />
            <Input
              label="Normal Working Hours"
              type="number"
              value={formData.normalWorkingHours}
              onChange={(e) => setFormData({ ...formData, normalWorkingHours: parseInt(e.target.value) })}
            />
            <Input
              label="Lunch Break (mins)"
              type="number"
              value={formData.lunchBreakDuration}
              onChange={(e) => setFormData({ ...formData, lunchBreakDuration: parseInt(e.target.value) })}
            />
            <Input
              label="Half Day Hours"
              type="number"
              value={formData.halfDayHours}
              onChange={(e) => setFormData({ ...formData, halfDayHours: parseInt(e.target.value) })}
            />
          </div>

          <h3 className="text-sm font-semibold text-gray-700 pt-2 border-t">Lateness / Early-Out</h3>
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Lateness Grace Period (mins)"
              type="number"
              value={formData.latenessGracePeriod}
              onChange={(e) => setFormData({ ...formData, latenessGracePeriod: parseInt(e.target.value) })}
            />
            <Input
              label="Lateness Deduction Rate"
              type="number"
              step="0.01"
              value={formData.latenessDeductionRate}
              onChange={(e) => setFormData({ ...formData, latenessDeductionRate: parseFloat(e.target.value) })}
            />
            <Input
              label="Early-Out Grace Period (mins)"
              type="number"
              value={formData.earlyOutGracePeriod}
              onChange={(e) => setFormData({ ...formData, earlyOutGracePeriod: parseInt(e.target.value) })}
            />
            <Input
              label="Early-Out Deduction Rate"
              type="number"
              step="0.01"
              value={formData.earlyOutDeductionRate}
              onChange={(e) => setFormData({ ...formData, earlyOutDeductionRate: parseFloat(e.target.value) })}
            />
            <label className="flex items-center gap-2 text-sm col-span-2">
              <input
                type="checkbox"
                checked={formData.excludeGracePeriod}
                onChange={(e) => setFormData({ ...formData, excludeGracePeriod: e.target.checked })}
              />
              Exclude grace period (lateness measured from clock-in time directly)
            </label>
          </div>

          <h3 className="text-sm font-semibold text-gray-700 pt-2 border-t">Overtime</h3>
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="OT Rate (multiplier)"
              type="number"
              step="0.5"
              value={formData.otRate}
              onChange={(e) => setFormData({ ...formData, otRate: parseFloat(e.target.value) })}
            />
            <Input
              label="OT Round Block (mins)"
              type="number"
              value={formData.otRoundValue}
              onChange={(e) => setFormData({ ...formData, otRoundValue: parseInt(e.target.value) })}
            />
            <Input
              label="Minimum OT (hours to count)"
              type="number"
              step="0.5"
              value={formData.minimumOt}
              onChange={(e) => setFormData({ ...formData, minimumOt: parseFloat(e.target.value) })}
            />
            <Input
              label="Max OT Hours (before extra rate)"
              type="number"
              step="0.5"
              placeholder="optional"
              value={formData.maxOtHours}
              onChange={(e) => setFormData({ ...formData, maxOtHours: e.target.value })}
            />
            <Input
              label="Extra OT Rate (beyond max hours)"
              type="number"
              step="0.5"
              placeholder="optional"
              value={formData.extraOtRate}
              onChange={(e) => setFormData({ ...formData, extraOtRate: e.target.value })}
            />
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={formData.otRoundUp}
                onChange={(e) => setFormData({ ...formData, otRoundUp: e.target.checked })}
              />
              Round OT up (vs. down) to block size
            </label>
          </div>

          <Button type="submit" className="w-full">
            {editingId ? 'Save Changes' : 'Create Shift'}
          </Button>
        </form>
      </Modal>
    </div>
  );
}
